
import type { ActionType } from '@fleetduel/shared'
import { ChargeCounter } from './ChargeCounter.js'
import { DirectionPicker } from './DirectionPicker.js'
import { useUIStore } from '../../store/uiStore.js'
import { useGameStore } from '../../store/gameStore.js'
import { ACTION_ICONS } from '../../assets/index.js'

const actions: { type: ActionType; label: string }[] = [
  { type: 'strike', label: 'Strike' },
  { type: 'torpedo', label: 'Torpedo' },
  { type: 'sonar', label: 'Sonar' },
]

interface ActionBarProps {
  vertical?: boolean
  /** Dense one-row layout for small screens: icon buttons with charge badges */
  compact?: boolean
}

export function ActionBar({ vertical = false, compact = false }: ActionBarProps) {
  const selectedAction = useUIStore(s => s.selectedAction)
  const setSelectedAction = useUIStore(s => s.setSelectedAction)
  const torpedoDirection = useUIStore(s => s.torpedoDirection)
  const setTorpedoDirection = useUIStore(s => s.setTorpedoDirection)
  const torpedoCharges = useGameStore(s => s.torpedoCharges)
  const sonarCharges = useGameStore(s => s.sonarCharges)

  if (compact) {
    const charges: Partial<Record<ActionType, number>> = {
      torpedo: torpedoCharges,
      sonar: sonarCharges,
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          {actions.map(({ type, label }) => {
            const charge = charges[type]
            const disabled = charge !== undefined && charge <= 0
            return (
              <button
                key={type}
                onClick={() => !disabled && setSelectedAction(type)}
                disabled={disabled}
                aria-label={label}
                style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  padding: '6px 10px 4px',
                  minWidth: 62,
                  fontSize: 10,
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: 6,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  backgroundColor: selectedAction === type ? '#1976d2' : '#263238',
                  color: disabled ? '#546e7a' : '#fff',
                  opacity: disabled ? 0.5 : 1,
                  boxShadow: selectedAction === type ? '0 0 8px rgba(79, 195, 247, 0.4)' : 'none',
                }}
              >
                <img
                  src={ACTION_ICONS[type]}
                  alt=""
                  style={{ width: 22, height: 22, opacity: disabled ? 0.3 : 1 }}
                  draggable={false}
                />
                {label}
                {charge !== undefined && (
                  <span style={{
                    position: 'absolute',
                    top: 2,
                    right: 4,
                    fontSize: 10,
                    fontWeight: 700,
                    color: charge > 0 ? '#4fc3f7' : '#546e7a',
                  }}>
                    ×{charge}
                  </span>
                )}
              </button>
            )
          })}
        </div>
        {/* Slot always reserves its height so selecting/deselecting Torpedo
            never shifts the layout */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          height: 28,
          visibility: selectedAction === 'torpedo' ? 'visible' : 'hidden',
        }}>
          <DirectionPicker direction={torpedoDirection} onChange={setTorpedoDirection} />
        </div>
      </div>
    )
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 8,
      padding: '12px 0',
    }}>
      {/* Action buttons */}
      <div style={{
        display: 'flex',
        flexDirection: vertical ? 'column' : 'row',
        gap: 8,
        justifyContent: 'center',
      }}>
        {actions.map(({ type, label }) => {
          const disabled =
            (type === 'torpedo' && torpedoCharges <= 0) ||
            (type === 'sonar' && sonarCharges <= 0)

          return (
            <button
              key={type}
              onClick={() => !disabled && setSelectedAction(type)}
              disabled={disabled}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 20px',
                fontSize: 14,
                fontWeight: 600,
                border: 'none',
                borderRadius: 6,
                cursor: disabled ? 'not-allowed' : 'pointer',
                backgroundColor: selectedAction === type ? '#1976d2' : '#263238',
                color: disabled ? '#546e7a' : '#fff',
                opacity: disabled ? 0.5 : 1,
                width: vertical ? 140 : 'auto',
                minWidth: vertical ? undefined : 80,
                boxShadow: selectedAction === type ? '0 0 8px rgba(79, 195, 247, 0.4)' : 'none',
              }}
            >
              <img
                src={ACTION_ICONS[type]}
                alt={label}
                style={{
                  width: 28,
                  height: 28,
                  opacity: disabled ? 0.3 : 1,
                }}
                draggable={false}
              />
              {label}
            </button>
          )
        })}
      </div>

      {/* Charge counters */}
      <div style={{
        display: 'flex',
        flexDirection: vertical ? 'column' : 'row',
        gap: vertical ? 4 : 16,
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <ChargeCounter label="Torpedo" count={torpedoCharges} maxCount={2} />
        <ChargeCounter label="Sonar" count={sonarCharges} maxCount={2} />
      </div>

      {/* Direction picker for torpedo — slot always reserves its height so
          selecting/deselecting Torpedo never shifts the layout */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        height: 30,
        visibility: selectedAction === 'torpedo' ? 'visible' : 'hidden',
      }}>
        <DirectionPicker direction={torpedoDirection} onChange={setTorpedoDirection} />
      </div>
    </div>
  )
}
